// gtag.js loader + thin typed wrapper around `window.gtag`.
// One-time script injection per primary tag id; dual-tag is the marketing
// site's GA4 property. Both `config` calls disable auto page_view because v3
// owns pageview firing through `initAnalytics`.

type GtagFn = NonNullable<Window['gtag']>;

let bootstrapped = false;

export function isGtagReady(): boolean {
  return bootstrapped && typeof window.gtag === 'function';
}

export function bootstrapGtag(primary: string, secondary?: string): void {
  if (bootstrapped || !primary) return;
  bootstrapped = true;
  window.dataLayer = window.dataLayer ?? [];
  // gtag.js only acts on an Arguments object; a pushed array is ignored, so
  // nothing (config, events, page views) would ever be sent.
  const gtag = function () {
    (window.dataLayer ??= []).push(arguments);
  } as GtagFn;
  window.gtag = gtag;
  // Stamp the bootstrap moment; required by gtag.js per Google docs.
  gtag('js', new Date());
  // send_page_view: false — v3 owns pageview firing via router.afterEach
  // (initAnalytics). Auto-tracking would double-count or fire on stale
  // routes during the SPA boot sequence.
  gtag('config', primary, { send_page_view: false });
  if (secondary) gtag('config', secondary, { send_page_view: false });

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(primary)}`;
  document.head.appendChild(script);
}

export function gtag(...args: unknown[]): void {
  window.gtag?.(...args);
}

// Test-only — drops the bootstrapped latch so the next call re-injects.
export function __resetGtagForTests(): void {
  bootstrapped = false;
  delete window.dataLayer;
  delete window.gtag;
  // The injected loader tag outlives the latch — drop it too, or specs
  // sharing one jsdom document see each other's script element.
  for (const s of document.querySelectorAll('script[src*="googletagmanager.com/gtag/js"]')) {
    s.remove();
  }
}
